import { ErrorMessages, ImpactsActivity, ImpactsResourceType, MitigationTypes } from '@src/utils/enums';
import * as yup from 'yup';
import { decimalNumberRegex } from '@src/utils/regex';
import options from './impactsAndMitigation.options';

export const requiredActivity = [
  ImpactsActivity.ConversionWatersType,
  ImpactsActivity.DischargeDredgedMaterial,
  ImpactsActivity.DischargeFillMaterial,
  ImpactsActivity.EcologicalRestoration,
];

const valuesFromOptions = (opts = []) => opts.map((o) => o.value);

export const getAllowedAmountTypesForActivity = (activity) => {
  switch (activity) {
    case ImpactsActivity.ConversionWatersType:
    case ImpactsActivity.DischargeFillMaterial:
    case ImpactsActivity.DischargeDredgedMaterial:
    case ImpactsActivity.EcologicalRestoration:
      return ['Fill Area'];

    case ImpactsActivity.DredgingSection:
      return ['Removal Area', 'Removal Volume'];

    case ImpactsActivity.Other:
      return ['Fill Area', 'Removal Area', 'Structure Area'];

    case ImpactsActivity.Removal:
      return ['Removal Area'];

    case ImpactsActivity.Structure:
      return ['Structure Area'];

    case ImpactsActivity.TransportDredgedMaterial:
      return ['Fill Volume'];

    default:
      return ['Fill Area', 'Fill Volume', 'Removal Area', 'Removal Volume', 'Structure Area'];
  }
};

// Helper function to check if a value is actually provided
const isValueProvided = (val) => val !== null && val !== undefined && val !== '';

// Helper function to check if a value is actually provided and NOT zero
const isValidNumericValue = (val) => {
  if (!isValueProvided(val)) return false;
  return Number(val) > 0;
};

// Helper function to check if length and width form a valid tuple
const validateLengthWidthTuple = (value, widthValue) => {
  const lengthProvided = isValueProvided(value);
  const widthProvided = isValueProvided(widthValue);

  // Both must be provided or both must be empty (tuple validation)
  return lengthProvided === widthProvided;
};

export const impactsValidationSchema = yup.object().shape({
  waterName: yup.string().required(ErrorMessages.SelectOption),
  impactName: yup.string().required(ErrorMessages.Required),
  dischargeMaterialType: yup
    .string()
    .when('activity', {
      is: (val) => requiredActivity.includes(val) === true,
      then: () => yup.string().required(ErrorMessages.SelectOption),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
  activity: yup
    .string()
    .required(ErrorMessages.SelectOption)
    .oneOf(valuesFromOptions(options.impactActivities), ErrorMessages.SelectOption),
  resourceType: yup
    .string()
    .required(ErrorMessages.SelectOption)
    .oneOf(valuesFromOptions(options.impactResourceTypes), ErrorMessages.SelectOption),
  isPermanentLoss: yup
    .string()
    .required(ErrorMessages.SelectOption)
    .oneOf(['true', 'false'], ErrorMessages.SelectOption),
  impactDuration: yup
    .string()
    .required(ErrorMessages.Required)
    .oneOf(['Temporary', 'Permanent'], ErrorMessages.SelectOption),
  amountType: yup
    .string()
    .required(ErrorMessages.SelectOption)
    .test('valid-amount-type', 'Amount Type is not valid for the selected Activity', function (value) {
      const { activity } = this.parent;
      if (!value || !activity) return true;
      return getAllowedAmountTypesForActivity(activity).includes(value);
    }),
  amountUnit: yup.string().required(ErrorMessages.Required),
  proposedLength: yup
    .string()
    .nullable()
    .matches(decimalNumberRegex, ErrorMessages.Invalid)
    .test(
      'length-width-tuple',
      'Proposed Length and Proposed Width must both be provided together or both be empty',
      function (value) {
        const { proposedWidth } = this.parent;
        return validateLengthWidthTuple(value, proposedWidth);
      }
    )
    .test('length-not-zero', 'Length must be greater than zero', function (value) {
      const { proposedWidth } = this.parent;
      if (isValueProvided(value) && isValueProvided(proposedWidth)) {
        return Number(value) > 0;
      }
      return true;
    }),
  proposedWidth: yup
    .string()
    .nullable()
    .matches(decimalNumberRegex, ErrorMessages.Invalid)
    .test(
      'length-width-tuple',
      'Proposed Length and Proposed Width must both be provided together or both be empty',
      function (value) {
        const { proposedLength } = this.parent;
        return validateLengthWidthTuple(proposedLength, value);
      }
    )
    .test('width-not-zero', 'Width must be greater than zero', function (value) {
      const { proposedLength } = this.parent;
      if (isValueProvided(proposedLength) && isValueProvided(value)) {
        return Number(value) > 0;
      }
      return true;
    }),
  proposedAmount: yup
    .string()
    .nullable()
    .matches(decimalNumberRegex, ErrorMessages.Invalid)
    .test('amount-not-zero', 'Amount must be greater than zero', function (value) {
      if (isValueProvided(value)) {
        return Number(value) > 0;
      }
      return true;
    })
    .test('proposed-amount-required', ErrorMessages.GreatThanZero, function (value) {
      const { proposedLength, proposedWidth, resourceType, activity, amountType, amountUnit } = this.parent;

      const tupleProvided = isValueProvided(proposedLength) && isValueProvided(proposedWidth);

      if (tupleProvided) {
        return true;
      }

      // Special case: Skip amount validation for Cubic Yards unit
      if (amountUnit === 'Cubic Yards') {
        return true;
      }

      const shouldRequireAmount =
        (!proposedLength && !proposedWidth) ||
        resourceType !== ImpactsResourceType.RiverStream ||
        activity === ImpactsActivity.TransportDredgedMaterial ||
        amountType === 'Removal Volume';

      if (shouldRequireAmount) {
        return isValidNumericValue(value);
      }

      return true;
    })
    .test(
      'river-stream-validation',
      'Proposed Amount cannot be specified for this River/Stream configuration. Please remove this value and enter Proposed Length and Proposed Width instead.',
      function (value) {
        const { resourceType, activity, amountType, proposedLength, proposedWidth } = this.parent;

        const tupleProvided = isValueProvided(proposedLength) && isValueProvided(proposedWidth);

        if (tupleProvided) {
          return true;
        }

        if (resourceType !== ImpactsResourceType.RiverStream) {
          return true; // Non-River/Stream resources can use amount without L/W
        }

        // These activities/amountTypes REQUIRE L/W to be provided.
        const isInvalidActivity =
          activity !== ImpactsActivity.TransportDredgedMaterial && activity !== ImpactsActivity.DredgingSection;
        const isRemovalArea = amountType === 'Removal Area';

        if (isInvalidActivity || isRemovalArea) {
          return !isValueProvided(value);
        }

        return true;
      }
    ),
});

export const ilfValidationSchema = yup.object().shape({
  mitigationName: yup.string().required(ErrorMessages.Required),
  mitigationType: yup.string().required(ErrorMessages.SelectOption),
  nameOfBankOrILF: yup.string().required(ErrorMessages.SelectOption),
  creditUnit: yup.string().nullable(),
  proposedCreditAmount: yup.number().nullable(),
});

export const prmValidationSchema = yup.object().shape({
  waterName: yup
    .string()
    .when('mitigationType', {
      is: (val) => val !== MitigationTypes.AdvancedPermitteeResponsible,
      then: () => yup.string().required(ErrorMessages.SelectOption),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
  mitigationName: yup.string().required(ErrorMessages.Required),
  mitigationType: yup.string().required(ErrorMessages.SelectOption),
  permitteeResponsibleType: yup.string().nullable(),
  resourceType: yup.string().nullable(),
  proposedLength: yup
    .string()
    .test('zeroTest', ErrorMessages.GreatThanZero, function (item) {
      return !this.parent.proposedAmount || this.parent.proposedAmount === 0 ? item > 0 : true;
    })
    .nullable(),
  proposedWidth: yup
    .string()
    .test('zeroTest', ErrorMessages.GreatThanZero, function (item) {
      return !this.parent.proposedAmount || this.parent.proposedAmount === 0 ? item > 0 : true;
    })
    .nullable(),
  proposedAmount: yup
    .string()
    .when(['proposedLength', 'proposedWidth'], {
      is: (proposedLength, proposedWidth) => !proposedLength && !proposedWidth,
      then: () => yup.string().required(ErrorMessages.Required).min(1, ErrorMessages.GreatThanZero),
    })
    .nullable(),
  amountUnit: yup.string().required(ErrorMessages.SelectOption),
});

export const getIpSchema = ({
  ilfTableErrors,
  impactsTableErrors,
  impactsTableLength,
  prmTableErrors,
  totalMitigationTableLength,
}) =>
  yup.object().shape({
    impactsTable: yup
      .string()
      .test('impacts-table-test', 'Impacts: At least one impact is required', function () {
        return impactsTableLength >= 1;
      })
      .test('impact-table-errors-test', 'Impacts: Table contains invalid rows', function () {
        return impactsTableErrors === false;
      }),
    impactAdditionalDetails: yup.string().required(ErrorMessages.Required),
    proposedMitigationMeasures: yup.string().required(ErrorMessages.Required),
    isCompensatoryMitigationProposed: yup.string().required(ErrorMessages.SelectOption),
    mitigationTable: yup.string().when('isCompensatoryMitigationProposed', {
      is: 'true',
      then: () =>
        yup
          .string()
          .test('mitigation-table-test', 'Mitigation: At least one mitigation is required', function () {
            return totalMitigationTableLength >= 1;
          })
          .test('mitigation-table-errors-test', 'Mitigation: Table contains invalid rows', function () {
            return prmTableErrors === false && ilfTableErrors === false;
          }),
      otherwise: () => yup.string().nullable(),
    }),
    compensatoryMitigationExplanation: yup
      .string()
      .nullable()
      .when('willResultInWetlandLoss', { is: 'true', then: () => yup.string().required(ErrorMessages.Required) }),
  });
export const getGpSchema = ({
  ilfTableErrors,
  impactsTableErrors,
  impactsTableLength,
  prmTableErrors,
  totalMitigationTableLength,
}) =>
  yup.object().shape({
    impactsTable: yup
      .string()
      .test('impacts-table-test', 'Impacts: At least one impact is required', function () {
        return impactsTableLength >= 1;
      })
      .test('impact-table-errors-test', 'Impacts: Table contains invalid rows', function () {
        return impactsTableErrors === false;
      }),
    impactAdditionalDetails: yup.string().required(ErrorMessages.Required),
    proposedMitigationMeasures: yup.string().required(ErrorMessages.Required),
    willResultInWetlandLoss: yup.string().required(ErrorMessages.SelectOption), /// NEEDS TO BE REQUIRED FOR GP AND NOT EXIST FOR IP
    isCompensatoryMitigationProposed: yup.string().required(ErrorMessages.SelectOption),
    mitigationTable: yup.string().when('isCompensatoryMitigationProposed', {
      is: 'true',
      then: () =>
        yup
          .string()
          .test('mitigation-table-test', 'Mitigation: At least one mitigation is required', function () {
            return totalMitigationTableLength >= 1;
          })
          .test('impact-table-errors-test', 'Mitigation: Table contains invalid rows', function () {
            return prmTableErrors === false && ilfTableErrors === false;
          }),
      otherwise: () => yup.string().nullable(),
    }),
    compensatoryMitigationExplanation: yup
      .string()
      .nullable()
      .when('willResultInWetlandLoss', {
        is: 'true',
        then: () => yup.string().required(ErrorMessages.Required),
      }),
  });

export const getDefaultValues = (permitData) => ({
  proposedAcres: permitData?.impactSummary?.proposedAcres ?? null,
  proposedLinearFeet: permitData?.impactSummary?.proposedLinearFeet ?? null,
  proposedCubicYards: permitData?.impactSummary?.CubicYards ?? null,
  impactAdditionalDetails: permitData?.impactSummary?.impactAdditionalDetails ?? '',
  isCompensatoryMitigationProposed:
    permitData?.isCompensatoryMitigationProposed !== undefined && permitData?.isCompensatoryMitigationProposed !== null
      ? permitData.isCompensatoryMitigationProposed
        ? 'true'
        : 'false'
      : '',
  compensatoryMitigationExplanation: permitData?.compensatoryMitigationExplanation ?? '',
  willResultInWetlandLoss:
    permitData?.willResultInWetlandLoss !== undefined && permitData?.willResultInWetlandLoss !== null
      ? permitData.willResultInWetlandLoss
        ? 'true'
        : 'false'
      : '',
  proposedMitigationMeasures: permitData?.proposedMitigationMeasures ?? '',
});
